import type { FieldValue } from 'features/libra/ontology/ontologyFieldTypes'
import isEqual from 'lodash/isEqual'

import { MISSING_VALUE_PLACEHOLDER } from './formatFieldValue'

/**
 * Shared classifier for comparing a user-side `FieldValue` against its LLM
 * lineage counterpart. Used by both the LLM diff drawer and the forthcoming
 * LLM stats compute module so the two views can't drift on definitions like
 * "accepted" vs "edited".
 *
 * Callers are expected to have already applied `stripArrayRowIds` to the
 * inner `value` of each wrapper before passing them in (consistent with the
 * drawer's pre-existing behavior). Without that, an array-of-objects on the
 * user side will always compare as `edited` because of the locally stamped
 * `_id` tokens that the LLM lineage never carries.
 *
 * Callers should also pre-compute `userText` / `llmText` via
 * `formatFieldValue` so "empty but wrapped" values (e.g. `''` or `[]`) are
 * treated as missing the same way the drawer already does.
 */

export type LlmFieldComparison =
  | 'accepted'
  | 'edited'
  | 'rejected'
  | 'userOnly'
  | 'both-missing'

export function classifyLlmField(params: {
  userFv: FieldValue | undefined
  llmFv: FieldValue | undefined
  userText: string
  llmText: string
}): LlmFieldComparison {
  const { userFv, llmFv, userText, llmText } = params

  const userPresent =
    userFv !== undefined && userText !== MISSING_VALUE_PLACEHOLDER
  const llmPresent =
    llmFv !== undefined && llmText !== MISSING_VALUE_PLACEHOLDER

  if (!userPresent && !llmPresent) return 'both-missing'
  if (userPresent && !llmPresent) return 'userOnly'
  if (!userPresent && llmPresent) return 'rejected'

  return isEqual(userFv?.value, llmFv?.value) ? 'accepted' : 'edited'
}
